import React, { Context, useContext, useEffect, useState } from 'react'
import { QUERY_EMPLOYEES, CREATE_EMPLOYEE, DELETE_EMPLOYEE, UPDATE_EMPLOYEE, UPDATE_EMPLOYEE_PASSWORD } from './graphql'
import axios from 'axios'

import { Employee, CreateEmployeeVariables, EmployeesQueryParams, EmployeesQueryParamValues } from 'models'
import { useQuery, useMutation } from '@apollo/client'
import Notification from 'antd/es/notification'

export type State = {
  data: Employee[] | undefined
  loading: boolean
  refetch: any

  queryParams: EmployeesQueryParams
  setQueryParams: (queryParams: EmployeesQueryParams) => void
  setQueryParamValues: (values: EmployeesQueryParamValues[]) => void

  createEmployee: (employee: CreateEmployeeVariables) => Promise<Employee>
  deleteEmployee: (_id: string) => Promise<boolean>
  updateEmployee: (employee: Employee) => Promise<boolean>
  uploadTaxCard: (taxCard: { card_image: any; userId: string }) => Promise<any>
  updatePassword: (payload: UpdatePasswordVariables) => Promise<boolean>
}

type UpdatePasswordVariables = {
  _id: string
  password: string
}

const shadowContext = React.createContext<State | undefined>(undefined)

export const context = shadowContext as Context<State>

export const useEmployees = () => useContext(context)

export const EmployeesProvider = ({ children }: { children?: React.ReactNode }) => {
  const [queryParams, setQueryParams] = useState<EmployeesQueryParams>({})

  const setQueryParamValues = (values: EmployeesQueryParamValues[]) => {
    const current: EmployeesQueryParams = { ...queryParams }

    values.forEach((item) => {
      if (item.value) {
        current[item.key] = item.value
      } else {
        delete current[item.key]
      }
    })

    setQueryParams(current)
  }

  const { loading, data, refetch } = useQuery<{ employees: Employee[] }>(QUERY_EMPLOYEES, {
    variables: { ...queryParams }
  })

  useEffect(() => {
    refetch()
  }, [queryParams, refetch])

  const [_createMutation] = useMutation<{ createEmployee: Employee }, { employee: CreateEmployeeVariables }>(CREATE_EMPLOYEE)
  const [_deleteMutation] = useMutation<{ deleteEmployee: boolean }, { _id: string }>(DELETE_EMPLOYEE)
  const [_updateMutation] = useMutation<{ updateEmployee: boolean }, { employee: Employee }>(UPDATE_EMPLOYEE)
  const [_updatePasswordMutation] = useMutation<{ updateEmployeePassword: boolean }, { _id: string; password: string }>(UPDATE_EMPLOYEE_PASSWORD)

  const uploadTaxCard = async (taxCard: { card_image: any; userId: string }): Promise<any> => {
    try {
      await axios.post(global.Config.backendUri + '/taxCardUpload', { ...taxCard })
      refetch()
    } catch (e) {
      console.log(e)
    }
  }

  const createEmployee = async (employee: CreateEmployeeVariables): Promise<Employee> => {
    try {
      const { data, errors } = await _createMutation({ variables: { employee } })
      if (data?.createEmployee) {
        refetch()
        Notification.success({ message: 'Luotiin työntekijä', placement: 'bottomLeft' })
        return data?.createEmployee
      } else {
        throw errors
      }
    } catch (e) {
      Notification.error({ message: 'Ei voitu luoda työntekijää' + JSON.stringify(e), placement: 'bottomLeft' })
      throw e
    }
  }

  const updateEmployee = async (employee: Employee): Promise<boolean> => {
    try {
      const stripped = employee
      delete stripped.password
      const { data, errors } = await _updateMutation({ variables: { employee: stripped } })
      if (data?.updateEmployee) {
        Notification.success({ message: 'Päivitettiin työntekijä', placement: 'bottomLeft' })
        refetch()
        return data?.updateEmployee
      } else {
        throw errors
      }
    } catch (e) {
      Notification.error({ message: 'Ei voitu päivittää työntekijää' + JSON.stringify(e), placement: 'bottomLeft' })
      throw e
    }
  }

  const deleteEmployee = async (_id: string): Promise<boolean> => {
    try {
      const { data, errors } = await _deleteMutation({ variables: { _id } })
      if (data?.deleteEmployee) {
        refetch()
        Notification.success({ message: 'Poistettiin työntekijä', placement: 'bottomLeft' })
        return data?.deleteEmployee
      } else {
        throw errors
      }
    } catch (e) {
      Notification.error({ message: 'Ei voitu poistaa työntekijää' + JSON.stringify(e), placement: 'bottomLeft' })
      throw e
    }
  }

  const updatePassword = async (payload: UpdatePasswordVariables): Promise<boolean> => {
    try {
      const { data, errors } = await _updatePasswordMutation({ variables: { ...payload } })
      if (data?.updateEmployeePassword) {
        refetch()
        Notification.success({ message: 'Päivitettiin salasana', placement: 'bottomLeft' })
        return data?.updateEmployeePassword
      } else {
        throw errors
      }
    } catch (e) {
      Notification.error({ message: 'Ei päivitetty salasanaa', placement: 'bottomLeft' })
      throw e
    }
  }

  const { Provider } = context

  return (
    <Provider
      value={{
        data: data?.employees,
        loading,
        refetch,

        queryParams,
        setQueryParams,
        setQueryParamValues,

        createEmployee,
        updateEmployee,
        deleteEmployee,
        uploadTaxCard,
        updatePassword
      }}
    >
      {children}
    </Provider>
  )
}
